import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { toast } from 'sonner';
import { Users, Copy, Ban, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  contacted: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  qualified: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  won: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  lost: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const LeadRow = ({ lead, statuses, onPatch }) => {
  const [notes, setNotes] = useState(lead.notes || '');
  const expired = lead.magic_expires_at && new Date(lead.magic_expires_at) < new Date();
  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/demo/launch?token=${lead.magic_token}`);
    toast.success('Magic link copied');
  };
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`lead-row-${lead.id}`}>
      <td className="py-3 px-3">
        <p className="font-semibold text-white">{lead.name}{lead.company ? <span className="text-white/40 font-normal"> · {lead.company}</span> : null}</p>
        <p className="text-xs text-white/40">{new Date(lead.created_at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}</p>
        <span className={`inline-block mt-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${lead.type === 'contact' ? 'bg-amber-500/20 text-amber-300' : 'bg-teal-500/20 text-teal-300'}`} data-testid={`lead-type-${lead.id}`}>
          {lead.type === 'contact' ? 'contact' : 'demo'}
        </span>
      </td>
      <td className="py-3 px-3 text-white/70 text-xs">
        <p>{lead.email}</p>
        <p className="text-white/40">{lead.phone}</p>
        {lead.message ? <p className="text-white/50 italic mt-1 max-w-[220px] truncate" title={lead.message}>“{lead.message}”</p> : null}
      </td>
      <td className="py-3 px-3"><span className="text-xs font-semibold uppercase tracking-wide text-teal-300">{lead.vertical}</span></td>
      <td className="py-3 px-3">
        <select value={lead.status || 'new'} onChange={(e) => onPatch(lead.id, { status: e.target.value })}
          className={`text-xs font-bold rounded-full border px-2.5 py-1 bg-transparent cursor-pointer ${STATUS_STYLES[lead.status || 'new']}`}
          data-testid={`lead-status-${lead.id}`}>
          {statuses.map((s) => <option key={s} value={s} className="bg-slate-900 text-white">{s}</option>)}
        </select>
      </td>
      <td className="py-3 px-3 text-center">
        <span className={`text-sm font-bold ${lead.logins ? 'text-emerald-400' : 'text-white/30'}`} data-testid={`lead-logins-${lead.id}`}>{lead.logins || 0}</span>
      </td>
      <td className="py-3 px-3">
        <div className="flex items-center gap-1.5">
          {expired ? <span className="text-[11px] text-red-400/80 font-semibold">expired</span> : (
            <>
              <button onClick={copyLink} title="Copy magic link" className="p-1.5 rounded-md bg-white/5 hover:bg-white/15 text-white/60" data-testid={`lead-copy-${lead.id}`}>
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onPatch(lead.id, { revoke: true })} title="Revoke link" className="p-1.5 rounded-md bg-white/5 hover:bg-red-500/20 text-white/60 hover:text-red-300" data-testid={`lead-revoke-${lead.id}`}>
                <Ban className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </td>
      <td className="py-3 px-3 w-64">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={1}
          onBlur={() => notes !== (lead.notes || '') && onPatch(lead.id, { notes })}
          placeholder="Notes…"
          className="w-full bg-black/20 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white/80 focus:outline-none focus:ring-1 focus:ring-teal-400 resize-none"
          data-testid={`lead-notes-${lead.id}`} />
      </td>
    </tr>
  );
};

export default function AdminDemoLeadsPage() {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/admin/demo/leads`).then((r) => setData(r.data)).catch(() => toast.error('Failed to load leads'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onPatch = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/demo/leads/${id}`, patch);
      toast.success(patch.revoke ? 'Link revoked' : 'Lead updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const leads = data?.leads || [];
  const counts = leads.reduce((a, l) => { a[l.status || 'new'] = (a[l.status || 'new'] || 0) + 1; return a; }, {});

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl" data-testid="admin-demo-leads-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users className="w-6 h-6 text-teal-400" /> Demo Leads</h1>
            <p className="text-sm text-white/40 mt-1">Prospects who requested demo access from the microsites. Work them left to right: contacted → qualified → won.</p>
          </div>
          <button onClick={load} className="p-2 rounded-lg bg-white/5 hover:bg-white/15 text-white/60" data-testid="leads-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap" data-testid="leads-status-summary">
          {(data?.statuses || []).map((s) => (
            <span key={s} className={`text-xs font-bold rounded-full border px-3 py-1 ${STATUS_STYLES[s]}`}>{s}: {counts[s] || 0}</span>
          ))}
        </div>
        <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.03] overflow-x-auto">
          {!data ? <p className="p-6 text-white/40">Loading…</p> : leads.length === 0 ? (
            <p className="p-6 text-white/40" data-testid="leads-empty">No demo leads yet — they appear when a prospect fills the access form on /demo.</p>
          ) : (
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-white/40 border-b border-white/10">
                  <th className="py-2.5 px-3">Lead</th><th className="py-2.5 px-3">Contact</th><th className="py-2.5 px-3">Vertical</th>
                  <th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3 text-center">Logins</th><th className="py-2.5 px-3">Magic link</th><th className="py-2.5 px-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => <LeadRow key={l.id} lead={l} statuses={data.statuses} onPatch={onPatch} />)}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
